import React, {useRef, useLayoutEffect, useMemo, forwardRef, useImperativeHandle, ReactElement, Ref} from 'react'

import TableHeader from './TableHeader.js'
import TableRow from './TableRow.js'
import TablePagination from './TablePagination.js'
import {P, Spinner} from '../index.js'
import {CellLayout, InternalColumn, TableProps, TableRef, TableRowData} from './types.js'
import {footerStyle, tableColors, tableColumns, tableMenus, tableSizes, tableSpacing} from './styles.js'
import t from '../../../helpers/Languages.js'
import {getColumnIds, isSortable} from './utils.js'
import useTableSort from './hooks/useTableSort.js'
import useStableArray from './hooks/useStableArray.js'
import useTablePagination from './hooks/useTablePagination.js'
import useTableSelection from './hooks/useTableSelection.js'
import useColumnLayout, {LayoutColumn} from './hooks/useColumnLayout.js'
import useTableSettings, {getStoredColumn, hasStoredSettings} from './hooks/useTableSettings.js'

const TableInner = <T extends TableRowData>(props: TableProps<T>, ref: Ref<TableRef>) => {
    const {
        tableName,
        legacyTableName,
        columns: callerColumns,
        rows: unstableRows,
        selectedRows,
        onChange,
        loading = false,
        emptyText,
        rowBackground,
        serverSidePagination = false,
        noPagination = false,
        totalRowsCount,
        onPagination,
        defaultSort,
        sortColumnIndex: sortColumnIndexProp,
        sortDecending: sortDecendingProp,
        style = {}
    } = props

    // The same rows in a new array (Object.values on every render) are the same rows: no new sort
    const rows = useStableArray(unstableRows)

    const tableRef = useRef<HTMLDivElement>(null)

    const {settings, updateColumns, clearVisible, setSort: storeSort, setOption, reset} = useTableSettings(tableName, callerColumns, legacyTableName)

    const totalRows = serverSidePagination ? totalRowsCount : rows.length
    const {page, pageSize, nrOfPages, goToPage, changePageSize, selectPage} = useTablePagination({totalRows, loading, onPagination})

    useImperativeHandle(ref, () => ({selectPage}), [selectPage])

    // The caller's columns with the settings: the stored width, else the caller's width; the user's visibility
    // choice, else the caller's visible
    const columns = useMemo<InternalColumn<T>[]>(() => {
        const ids = getColumnIds(callerColumns)

        return callerColumns.map((column, index) => {
            const stored = getStoredColumn(settings, ids[index], column.title)
            const minWidth = Math.max(tableColumns.minWidth, column.minWidth ?? 0)

            return {
                ...column,
                id: ids[index],
                visible: stored?.visible ?? !!column.visible,
                width: Math.max(minWidth, stored?.width ?? column.width ?? tableColumns.defaultWidth),
                flex: column.flex ?? tableColumns.defaultFlex,
                minWidth
            }
        })
    }, [callerColumns, settings])

    // When the caller changes a column's visible (a switch on the page), that wins over the choice in the settings
    const callerVisibleRef = useRef<Map<string, boolean>>(null)

    useLayoutEffect(() => {
        const previous = callerVisibleRef.current
        const changed: string[] = []

        callerVisibleRef.current = new Map(columns.map((column, index) => {
            const visible = !!callerColumns[index].visible

            if (previous && previous.has(column.id) && previous.get(column.id) !== visible) {
                changed.push(column.id)

                if (column.title) {
                    changed.push(column.title)
                }
            }

            return [column.id, visible]
        }))

        if (changed.length) {
            clearVisible(changed)
        }
    }, [columns])

    const shownColumns = useMemo(() => columns.filter((column) => column.visible && !column.filterOnly), [columns])

    const layoutColumns = useMemo<LayoutColumn[]>(() => {
        return shownColumns.map(({id, width, flex, minWidth}) => ({id, width, flex, minWidth}))
    }, [shownColumns])

    // Stores the widths of all shown columns after a resize, so they stay as the user left them
    const onResizeEnd = (widths: Record<string, number>) => {
        updateColumns(Object.fromEntries(Object.entries(widths).map(([id, width]) => [id, {width}])))
    }

    const {widths, startResize, resizing, containerHeight} = useColumnLayout(tableRef, layoutColumns, onResizeEnd)
    // Header menus open below the header and may grow to the bottom of the table
    const menuMaxHeight = Math.max(containerHeight - tableSizes.headerHeight, tableMenus.minHeight)

    // A new array only when the columns or their widths change, so a row re-renders for its own changes only
    const widthsKey = shownColumns.map((column) => widths[column.id]).join(',')

    const cells = useMemo<CellLayout<T>[]>(() => {
        return shownColumns.map((column) => ({id: column.id, column, width: widths[column.id]}))
    }, [shownColumns, widthsKey])

    // The stored sort, else defaultSort (or the deprecated sortColumnIndex), else the first column
    const ids = columns.map((column) => column.id)
    const legacyDefaultSort = sortColumnIndexProp !== undefined && ids[sortColumnIndexProp] ? {column: ids[sortColumnIndexProp], descending: sortDecendingProp} : undefined
    const defaultSortState = defaultSort ?? legacyDefaultSort

    const sortCandidates = [
        settings.sort && {id: settings.sort.id, desc: settings.sort.desc},
        defaultSortState && {id: defaultSortState.column, desc: !!defaultSortState.descending},
        ids[0] && {id: ids[0], desc: false}
    ]

    const currentSort = sortCandidates.find((sort) => sort && ids.includes(sort.id))
    const currentSortColumn = currentSort && columns.find((column) => column.id === currentSort.id)
    const sortColumn = currentSortColumn && isSortable(currentSortColumn) ? currentSortColumn : undefined
    const sortDecending = !!currentSort?.desc

    const toggleColumn = (id: string) => {
        const column = columns.find((column) => column.id === id)

        if (column) {
            updateColumns({[id]: {visible: !column.visible}})
        }
    }

    // Clicking the sorted column again reverses it, another column sorts ascending
    const setSort = (id: string) => {
        storeSort({id, desc: id === sortColumn?.id ? !sortDecending : false})

        if (!serverSidePagination) {
            selectPage(1)
        }
    }

    // The chosen dropdown option of each column, from a legacy dropdown's own key when there's none yet
    const columnOptions: Record<string, string> = {}

    columns.forEach((column) => {
        if (column.dropdown) {
            const legacyKey = column.dropdown.localStorageKey || column.dropdown.legacyKey
            const legacyValue = legacyKey && typeof settings[legacyKey] === 'string' ? settings[legacyKey] as string : undefined
            const value = settings.options[column.id] ?? legacyValue ?? column.dropdown.defaultValue

            if (value !== undefined) {
                columnOptions[column.id] = value
            }
        }
    })

    const sortedRows = useTableSort(rows, sortColumn, sortDecending, sortColumn ? columnOptions[sortColumn.id] : undefined)

    const selectOption = (column: InternalColumn<T>, value: string) => {
        setOption(column.id, value, column.dropdown?.localStorageKey)

        if (!serverSidePagination) {
            selectPage(1)
        }

        column.dropdown?.onChange?.(value)

        // Legacy dropdowns store the option themselves as well (some in another table's key, which can be missing)
        try {
            column.dropdown?.onClick?.(value)
        } catch (error) {
            console.error(error)
        }
    }

    // Client side the table pages the sorted rows, server side rows is the page
    const pageRows = serverSidePagination || noPagination ? sortedRows : sortedRows.slice((page - 1) * pageSize, page * pageSize)

    const {selectedSet, selectedCount, allSelected, someSelected, toggleRow, toggleAll, addRows} = useTableSelection({rows: sortedRows, selectedRows, onChange, loading})

    // Server side totalRows is totalRowsCount: rows is one page
    const empty = !loading && !pageRows.length && !totalRows

    const addPageToSelection = () => {
        addRows(pageRows.map((row) => row._id))
    }

    return (
        <div ref={tableRef} role='table' aria-busy={loading || undefined} style={{display: 'flex', flexDirection: 'column', height: '100%', ...style}}>
            <TableHeader
                cells={cells}
                menuMaxHeight={menuMaxHeight}
                columns={columns}
                noPagination={noPagination}
                onSort={setSort}
                sortId={sortColumn?.id}
                sortDecending={sortDecending}
                canSelect={!!onChange}
                allRowsSelected={allSelected}
                someRowsSelected={someSelected}
                onToggleAll={toggleAll}
                onAddPage={addPageToSelection}
                pageSize={pageSize}
                onChangePageSize={changePageSize}
                onToggleColumn={toggleColumn}
                options={columnOptions}
                onSelectOption={selectOption}
                hasSettings={hasStoredSettings(settings)}
                onReset={reset}
                onResizeStart={startResize}
                resizing={resizing}
            />

            <div role='rowgroup' style={{display: 'flex', flexDirection: 'column', flex: '1 0px', overflowY: 'auto'}}>
                {loading ?
                    <div style={{marginRight: 'auto', marginLeft: 'auto', marginTop: tableSpacing.loadingTop}}>
                        <Spinner />
                    </div> :
                    empty ?
                        <div role='row' style={{display: 'flex', justifyContent: 'center', padding: tableSpacing.emptyPadding}}>
                            <div role='cell' aria-colspan={cells.length + (onChange ? 1 : 0)} style={{textAlign: 'center'}}>
                                {emptyText === undefined || typeof emptyText === 'string' ?
                                    <P style={{color: tableColors.emptyText}}>{emptyText ?? t('tableNoResults')}</P> :
                                    emptyText
                                }
                            </div>
                        </div> :
                        pageRows.map((row) => (
                            <TableRow
                                key={row._id}
                                row={row}
                                cells={cells}
                                selected={selectedSet.has(row._id)}
                                onSelect={toggleRow}
                                background={rowBackground?.(row)}
                                canCheck={!!onChange}
                            />
                        ))
                }
            </div>

            <div style={footerStyle}>
                <div style={{flex: 1}} />

                {!noPagination &&
                    <TablePagination page={page} nrOfPages={nrOfPages} onSelectPage={goToPage} />
                }

                <div style={{display: 'flex', justifyContent: 'flex-end', flex: 1}}>
                    <P>{t('tableSelectedCount', {selected: selectedCount, total: totalRows ?? rows.length})}</P>
                </div>
            </div>
        </div>
    )
}

const ForwardedTable = forwardRef(TableInner)
ForwardedTable.displayName = 'Table'

// forwardRef loses the generic, the cast gives callers TableProps<T> for their row type plus the ref
const Table = ForwardedTable as <T extends TableRowData>(props: TableProps<T> & {ref?: Ref<TableRef>}) => ReactElement

export default Table
